import { useEffect, useRef } from "react"

interface Props {
  pickupLatitude: number
  pickupLongitude: number
  dropoffLatitude: number
  dropoffLongitude: number
}

interface LatLngPoint {
  lat: number
  lng: number
}

function decodeEncodedPolyline(encoded: string): LatLngPoint[] {
  const coordinates: LatLngPoint[] = []

  let index = 0
  let lat = 0
  let lng = 0

  while (index < encoded.length) {
    let b = 0
    let shift = 0
    let result = 0

    do {
      b = encoded.charCodeAt(index++) - 63
      result |= (b & 0x1f) << shift
      shift += 5
    } while (b >= 0x20)

    const dlat = (result & 1) !== 0 ? ~(result >> 1) : result >> 1
    lat += dlat

    shift = 0
    result = 0

    do {
      b = encoded.charCodeAt(index++) - 63
      result |= (b & 0x1f) << shift
      shift += 5
    } while (b >= 0x20)

    const dlng = (result & 1) !== 0 ? ~(result >> 1) : result >> 1
    lng += dlng

    coordinates.push({ lat: lat / 1e5, lng: lng / 1e5 })
  }

  return coordinates
}

export function CourierRouteMap({ pickupLatitude, pickupLongitude, dropoffLatitude, dropoffLongitude }: Props) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const routePolylineRef = useRef<google.maps.Polyline | null>(null)
  const pickupMarkerRef = useRef<google.maps.Marker | null>(null)
  const dropoffMarkerRef = useRef<google.maps.Marker | null>(null)

  useEffect(() => {
    const ensureMarkers = (origin: LatLngPoint, destination: LatLngPoint) => {
      if (!mapRef.current) {
        return
      }

      if (!pickupMarkerRef.current) {
        pickupMarkerRef.current = new google.maps.Marker({
          map: mapRef.current,
          position: origin,
          title: "Coleta",
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 6,
            fillColor: "#2563eb",
            fillOpacity: 1,
            strokeWeight: 1,
            strokeColor: "#ffffff",
          },
        })
      } else {
        pickupMarkerRef.current.setPosition(origin)
      }

      if (!dropoffMarkerRef.current) {
        dropoffMarkerRef.current = new google.maps.Marker({
          map: mapRef.current,
          position: destination,
          title: "Entrega",
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 6,
            fillColor: "#059669",
            fillOpacity: 1,
            strokeWeight: 1,
            strokeColor: "#ffffff",
          },
        })
      } else {
        dropoffMarkerRef.current.setPosition(destination)
      }
    }

    const drawPath = (path: LatLngPoint[]) => {
      if (!mapRef.current || path.length < 2) {
        return
      }

      if (!routePolylineRef.current) {
        routePolylineRef.current = new google.maps.Polyline({
          map: mapRef.current,
          strokeColor: "#2563eb",
          strokeOpacity: 0.9,
          strokeWeight: 5,
        })
      }

      routePolylineRef.current.setPath(path)

      const bounds = new google.maps.LatLngBounds()
      path.forEach((point) => bounds.extend(point))
      mapRef.current.fitBounds(bounds, 28)
    }

    const drawRoutesApiLine = async () => {
      if (!mapContainerRef.current || !window.google?.maps?.importLibrary) {
        return
      }

      const { Map } = (await google.maps.importLibrary("maps")) as google.maps.MapsLibrary

      if (!mapRef.current) {
        mapRef.current = new Map(mapContainerRef.current, {
          center: { lat: pickupLatitude, lng: pickupLongitude },
          zoom: 13,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          gestureHandling: "greedy",
          clickableIcons: false,
        })
      }

      const origin = { lat: pickupLatitude, lng: pickupLongitude }
      const destination = { lat: dropoffLatitude, lng: dropoffLongitude }

      ensureMarkers(origin, destination)

      try {
        const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY

        const response = await fetch("https://routes.googleapis.com/directions/v2:computeRoutes", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": apiKey,
            "X-Goog-FieldMask": "routes.polyline.encodedPolyline",
          },
          body: JSON.stringify({
            origin: {
              location: {
                latLng: {
                  latitude: pickupLatitude,
                  longitude: pickupLongitude,
                },
              },
            },
            destination: {
              location: {
                latLng: {
                  latitude: dropoffLatitude,
                  longitude: dropoffLongitude,
                },
              },
            },
            travelMode: "DRIVE",
            routingPreference: "TRAFFIC_AWARE",
          }),
        })

        if (!response.ok) {
          return
        }

        const data = await response.json()

        const encodedPolyline = data?.routes?.[0]?.polyline?.encodedPolyline

        if (typeof encodedPolyline !== "string" || encodedPolyline.length === 0) {
          return
        }

        const decodedPath = decodeEncodedPolyline(encodedPolyline)

        drawPath(decodedPath)
      } catch {
        return
      }
    }

    drawRoutesApiLine()
  }, [pickupLatitude, pickupLongitude, dropoffLatitude, dropoffLongitude])

  return <div ref={mapContainerRef} className="h-full w-full rounded-md border border-slate-300" />
}
